// The JET-SUIT visual: shown while player.gliding (the jet-suit glide the
// movement model ignites after a short time airborne, player.js).
import * as THREE from 'three';

// Worn, not deployed: a compact twin-turbine backpack with exhaust flames
// that throttle with airspeed. Follows the player while flying; the group's
// local +Z axis points along the travel direction, so the pack sits at -Z
// (behind the pilot) and the flames blast down-back.
export class JetSuit {
  constructor(scene) {
    this.group = new THREE.Group();
    this.group.name = 'jetsuit';
    this.group.visible = false;
    this.group.rotation.order = 'YXZ';
    this._lastYaw = 0;
    this._bank = 0;
    this._t = 0;
    const shell = new THREE.MeshLambertMaterial({ color: 0x33383f });
    const accent = new THREE.MeshLambertMaterial({ color: 0xd8452e });
    const pack = new THREE.Mesh(new THREE.BoxGeometry(0.44, 0.52, 0.2), shell);
    pack.position.set(0, 0, -0.28);
    this.group.add(pack);
    // stub wings — reads as "flying machine" from any angle
    const wing = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.03, 0.34), accent);
    wing.position.set(0, 0.12, -0.30);
    this.group.add(wing);
    this.flames = [];
    for (const sx of [-0.28, 0.28]) {
      const tur = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.115, 0.44, 8), shell);
      tur.position.set(sx, -0.14, -0.32);
      tur.rotation.z = -sx * 0.6;                    // nozzles splayed slightly out
      this.group.add(tur);
      // flame cone: geometry translated so scale.y stretches DOWN from the nozzle
      const fg = new THREE.ConeGeometry(0.085, 0.9, 8, 1, true);
      fg.rotateX(Math.PI);                           // apex down
      fg.translate(0, -0.45, 0);
      const flame = new THREE.Mesh(fg, new THREE.MeshBasicMaterial({
        color: 0x9fd4ff, transparent: true, opacity: 0.85, fog: false,
        depthWrite: false,
      }));
      flame.position.set(sx * 1.15, -0.34, -0.35);
      this.group.add(flame);
      this.flames.push(flame);
      const core = new THREE.Mesh(
        new THREE.ConeGeometry(0.04, 0.34, 6, 1, true).rotateX(Math.PI).translate(0, -0.17, 0),
        new THREE.MeshBasicMaterial({ color: 0xfff2c0, fog: false, depthWrite: false }));
      core.position.copy(flame.position);
      this.group.add(core);
      this.flames.push(core);
    }
    scene.add(this.group);
  }

  update(player, dt) {
    const on = player.gliding;
    this.group.visible = on;
    if (!on) return;
    this._t += dt;
    const yaw = Math.atan2(player.glideDir.x, player.glideDir.z);
    this.group.position.set(player.pos.x, player.pos.y + 1.05, player.pos.z);
    // bank into turns (rate-based, smoothed)
    let dy = yaw - this._lastYaw;
    if (dy > Math.PI) dy -= 2 * Math.PI;
    if (dy < -Math.PI) dy += 2 * Math.PI;
    const target = Math.max(-0.6, Math.min(0.6, (dy / Math.max(dt, 1e-3)) * 0.45));
    this._bank += (target - this._bank) * Math.min(1, dt * 6);
    this._lastYaw = yaw;
    const vmax = ((player.tun.glide.max_speed_kmh || 100) / 3.6);
    const th = Math.min(1, Math.hypot(player.vel.x, player.vel.y, player.vel.z) / vmax);
    // lean forward with speed, like a real jet-suit pilot
    this.group.rotation.set(0.12 + th * 0.55, yaw, -this._bank);
    for (let i = 0; i < this.flames.length; i++) {
      const fl = Math.max(0.3, th * 1.15) * (0.85 + 0.3 * Math.sin(this._t * 37 + i * 2.4));
      this.flames[i].scale.set(1, fl, 1);
    }
  }
}
